(() => {
  'use strict';
  const normalise = value => String(value ?? '').trim().toLowerCase();
  function optionIndex(product, kind) {
    return (product.options || []).findIndex(option => {
      const name = normalise(typeof option === 'string' ? option : option.name);
      return kind === 'size' ? name.includes('size') : name.includes('color') || name.includes('colour');
    });
  }
  function matchPiece(product, sourceSize, sourceColour) {
    const sizeIndex = optionIndex(product, 'size');
    const colourIndex = optionIndex(product, 'colour');
    const requestedSize = product.kind === 'pillow-case' ? 'Standard' : sourceSize;
    const sameSize = sizeIndex < 0 || !requestedSize ? [] : (product.variants || []).filter(variant => normalise(variant.options[sizeIndex]) === normalise(requestedSize));
    const sameColour = variant => colourIndex < 0 || normalise(variant.options[colourIndex]) === normalise(sourceColour);
    // Preserve an exact sold-out choice instead of silently replacing its colour.
    const variant = sameSize.find(sameColour) || sameSize.find(item => item.available) || sameSize[0] || null;
    const colour = variant && colourIndex >= 0 ? variant.options[colourIndex] : '';
    const size = variant && sizeIndex >= 0 ? variant.options[sizeIndex] : '';
    const colourFallback = Boolean(variant && colourIndex >= 0 && normalise(colour) !== normalise(sourceColour));
    let availability = '';
    if (!variant) availability = requestedSize ? `${requestedSize} is not offered for this piece.` : 'Choose a size on the product page.';
    else if (!variant.available) availability = 'Sold out in this selection.';
    else if (colourFallback) availability = `Alternative colour; ${sourceColour} is not offered in this size.`;
    return {variant, colour, size, requestedSize, colourFallback, availability};
  }
  function pieceLink(product, variant, contextUrl, view, currentHandle, optionsAnchor) {
    const context = new URL(contextUrl);
    const target = new URL(product.url, context.origin);
    // Only a variant belonging to this target product may enter its URL.
    const targetVariant = variant && (product.variants || []).find(item => String(item.id) === String(variant.id));
    if (targetVariant) target.searchParams.set('variant', targetVariant.id);
    else target.searchParams.delete('variant');
    if (product.kind === 'pillow-case') target.searchParams.delete('view');
    else if (view) target.searchParams.set('view', view);
    const previewTheme = context.searchParams.get('preview_theme_id');
    if (previewTheme) target.searchParams.set('preview_theme_id', previewTheme);
    const previewBar = context.searchParams.get('pb');
    if (previewBar !== null) target.searchParams.set('pb', previewBar);
    target.hash = product.handle === currentHandle && optionsAnchor ? optionsAnchor : '';
    return target.pathname + target.search + target.hash;
  }
  function pieceImage(product, colour) {
    const matching = (product.colourImages || []).find(image => normalise(image.colour) === normalise(colour) && image.src);
    return {src: matching?.src || product.fallbackImage || '', alt: matching ? `${product.title} in ${colour}` : product.title};
  }
  function includesLabel(kind, size) {
    if (kind === 'sleep-set') return size ? `1 top sheet + ${normalise(size) === 'single' ? '1 pillowcase' : '2 pillowcases'}` : 'Top sheet + pillowcase(s)';
    if (kind === 'top-sheet') return '1 top sheet';
    if (kind === 'fitted-sheet') return '1 fitted sheet';
    return '2 pillowcases';
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {optionIndex, matchPiece, pieceLink, pieceImage, includesLabel};
  if (typeof document === 'undefined') return;
  function init(root) {
    if (root.dataset.oxygenPiecesReady) return;
    const pdp = root.closest('.ss-approved-pdp');
    let source;
    try { source = JSON.parse(root.querySelector('[data-oxygen-pieces-source]').textContent); } catch (_) { return; }
    const cards = [...root.querySelectorAll('[data-oxygen-piece]')].map(card => {
      try { return {card, product: JSON.parse(card.querySelector('[data-oxygen-piece-data]').textContent)}; } catch (_) { return null; }
    }).filter(Boolean);
    if (!cards.length) return;
    root.dataset.oxygenPiecesReady = 'true';
    let previousVariant = '';
    function update(announce = true) {
      const selectedId = String(pdp?.dataset.selectedVariantId || source.selectedVariantId || '');
      if (selectedId === previousVariant) return;
      const sourceVariant = source.variants.find(variant => String(variant.id) === selectedId);
      if (!sourceVariant) return;
      previousVariant = selectedId;
      const size = sourceVariant.options[optionIndex(source, 'size')] || '';
      const colour = sourceVariant.options[optionIndex(source, 'colour')] || '';
      cards.forEach(({card, product}) => {
        const result = matchPiece(product, size, colour);
        const selected = result.variant;
        const current = product.handle === source.handle;
        const link = card.querySelector('[data-oxygen-piece-link]');
        link.href = pieceLink(product, selected, window.location.href, root.dataset.reviewView, source.handle, root.dataset.optionsAnchor);
        card.querySelector('[data-oxygen-piece-price]').textContent = selected ? selected.price : `From ${product.minimumPrice}`;
        card.querySelector('[data-oxygen-piece-selection]').textContent = selected ? [result.size, result.colour].filter(Boolean).join(' · ') : 'Choose size & colour';
        card.querySelector('[data-oxygen-piece-includes]').textContent = includesLabel(product.kind, result.size);
        const status = card.querySelector('[data-oxygen-piece-availability]');
        status.textContent = result.availability;
        status.hidden = !result.availability;
        card.querySelector('[data-oxygen-piece-cta]').textContent = current ? 'Choose your set' : selected?.available ? `Shop ${product.label.toLowerCase()}` : 'View options';
        const image = card.querySelector('.ss-oxygen-pieces__image');
        const nextImage = pieceImage(product, result.colour);
        if (image && nextImage.src) {
          if (image.getAttribute('src') !== nextImage.src) {
            const responsive = new URL(nextImage.src, window.location.origin);
            image.srcset = [240, 360, 540, 720].map(width => {
              responsive.searchParams.set('width', width);
              return `${responsive.href} ${width}w`;
            }).join(', ');
            image.src = nextImage.src;
          }
          image.alt = nextImage.alt;
        }
      });
      if (announce) root.querySelector('[data-oxygen-pieces-announcement]').textContent = (root.dataset.updateAnnouncement || '__selection__').replace('__selection__', [size, colour].filter(Boolean).join(', '));
    }
    update(false);
    if (pdp) {
      // Observe public variant state; never write to the main form or its selectors.
      new MutationObserver(() => update()).observe(pdp, {attributes: true, attributeFilter: ['data-selected-variant-id']});
      pdp.addEventListener('click', event => {
        if (event.target.closest('.ss-option-value')) queueMicrotask(() => update());
      });
    }
  }
  const initAll = () => document.querySelectorAll('[data-oxygen-pieces]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, {once: true});
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
